// What the Microsoft Teams settings page shows, from the account and the page's state. Pure, so the
// states are tested without the page: TeamsSyncSettings renders the result. Ported from the mobile
// app's utils/teamsSettingsModel.js (same states, rows and texts).
import { teamsProgress, teamsViewState } from "./teamsConnect";
import { teamsText } from "./teamsCore";

const fill = (text, vars) => String(text).replace(/\{\{\s*(\w+)\s*\}\}/g, (m, k) => (vars && vars[k] != null ? String(vars[k]) : m));

const formatWhen = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString();
};

/**
 * True while the user still has something on the Teams page to manage: a linked account (any state
 * but 'none'), a purge in progress or a read-only archive. Then the page stays reachable without an
 * active Kjuup.io plan (GET /teams/accounts and disconnect need no plan), so the user can always
 * disconnect and delete the imported chats.
 */
export function teamsHasAccount(account) {
  const { state, archived } = teamsViewState(account);
  return state !== "none" || archived;
}

/** statusReason of a consent_required account once an admin approved Kjuup for its organisation. */
export const TEAMS_CONSENT_GRANTED = "admin_consent_granted";

/**
 * The accounts the settings page lists (a user may connect several Microsoft accounts), oldest
 * first so a newly added one appears at the end. Rows with nothing to manage (fully removed, or a
 * failed connect that was dismissed) are left out.
 */
export function teamsVisibleAccounts(accounts) {
  const time = (a) => {
    const t = new Date(a.createdAt || 0).getTime();
    return Number.isNaN(t) ? 0 : t;
  };
  return (Array.isArray(accounts) ? accounts : []).filter((a) => a && teamsHasAccount(a)).sort((a, b) => time(a) - time(b));
}

/** The accounts a Teams chat can be started with (connected or still importing). */
export function teamsWritableAccounts(accounts) {
  return teamsVisibleAccounts(accounts).filter((a) => {
    const { state } = teamsViewState(a);
    return state === "connected" || state === "importing";
  });
}

/** Name of a Microsoft account in lists and pickers. */
export const teamsAccountLabel = (account) =>
  (account && (account.email || account.userPrincipalName || account.displayName)) || "Microsoft Teams";

/**
 * What the Settings "Microsoft Teams" row shows, from GET /teams/accounts (listTeamsAccounts):
 * { visible, connected, hasAccount, count } (null when the list failed). Visible once the server
 * enables Teams or while an account exists; `hasAccount` opens the page even when Kjuup.io is
 * locked. `connected` when any account is connected; `count` = accounts on the page.
 */
export function teamsSettingsRowState(res) {
  if (!res || !res.ok) return null;
  const list = teamsVisibleAccounts(res.accounts);
  const hasAccount = list.length > 0;
  const connected = teamsWritableAccounts(list).length > 0;
  return { visible: !!res.enabled || hasAccount, connected, hasAccount, count: list.length };
}

/**
 * input:
 *   account     TeamsAccount | null           enabled   server feature flag (GET /teams/accounts)
 *   locked      Kjuup.io lock text | null     error     { code, message } of the last connect attempt
 *   busy        { connecting, syncing, disconnecting, consent }
 *   handlers    { connect, sync, disconnect, purge, copyConsent, openConsent }
 *   strings     LanguageContext strings
 *   multi       one card of the account list (buildTeamsAccountsView): the account header also for
 *               an archive or a removal, no connect button for an archive (the page has its own
 *               "Add account"), and errors are left to the page
 * output: { state, banner, progress, account, note, primary, rows }
 */
export function buildTeamsSettingsView({ account, enabled = true, locked = null, error = null, busy = {}, handlers = {}, strings = null, multi = false }) {
  const tr = (key, fallback) => teamsText(strings, key, fallback);
  const { state, archived } = teamsViewState(account);
  // The IT admin approved Kjuup for the organisation (TeamsConsentService): only connecting is left.
  const approved = !!account && account.statusReason === TEAMS_CONSENT_GRANTED;
  const out = { state, banner: null, progress: null, account: null, note: null, primary: [], rows: [] };

  const connectLabel = busy.connecting ? tr("teams_connecting", "Connecting…") : tr("teams_connect", "Connect Microsoft Teams");
  const reconnectLabel = busy.connecting ? tr("teams_connecting", "Connecting…") : tr("teams_reconnect", "Reconnect Microsoft Teams");
  const connect = (label, kind = "primary") => ({ key: "connect", label, onPress: handlers.connect, busy: !!busy.connecting, kind, icon: "link" });
  const disconnectRow = {
    key: "disconnect",
    label: busy.disconnecting ? tr("teams_disconnecting", "Disconnecting…") : tr("teams_disconnect", "Disconnect"),
    icon: "link_off",
    danger: true,
    busy: !!busy.disconnecting,
    onPress: handlers.disconnect,
  };
  const syncRow = {
    key: "sync",
    label: busy.syncing ? tr("teams_syncing", "Syncing…") : tr("teams_sync_now", "Sync now"),
    icon: "sync",
    busy: !!busy.syncing,
    onPress: handlers.sync,
  };

  const linked = account && ((state !== "none" && state !== "removing") || (multi && (state === "removing" || archived)));
  if (linked) {
    const title = account.email || account.userPrincipalName || account.displayName || tr("teams_title", "Microsoft Teams");
    const when = formatWhen(account.lastSyncAt);
    const chip =
      state === "connected"
        ? { label: tr("v2_settings_connected", "Connected"), tone: "ok" }
        : state === "consent" && approved
          ? { label: tr("teams_chip_approved", "Approved"), tone: "ok" }
        : state === "importing" || state === "connecting"
          ? { label: tr("teams_chip_importing", "Importing"), tone: "muted" }
          : state === "consent" || state === "policy"
            ? { label: tr("teams_chip_not_connected", "Not connected"), tone: "warn" }
            : state === "removing"
              ? { label: tr("teams_chip_removing", "Removing"), tone: "muted" }
              : state === "none"
                ? { label: tr("teams_chip_archived", "Read-only"), tone: "muted" }
                : { label: tr("teams_chip_reconnect", "Reconnect"), tone: "err" };
    out.account = {
      caption: tr("teams_account_caption", "Microsoft account"),
      title,
      sub: when ? `${tr("teams_last_sync", "Last sync")}: ${when}` : (account.displayName && account.displayName !== title ? account.displayName : null),
      chip,
    };
  }

  const errorBanner = multi ? null : teamsErrorBanner(error, [state]);

  switch (state) {
    case "none":
      if (multi) {
        // The page's "Add account" connects; this card only manages the archive.
      } else if (!enabled) {
        out.banner = { tone: "info", title: tr("teams_not_available", "Microsoft Teams is not available for your account yet.") };
      } else if (!locked) {
        out.primary.push(connect(connectLabel));
      }
      if (archived) {
        out.note = tr("teams_archive_note", "Your imported Teams chats are kept as a read-only archive in Messages.");
        // The archive can be deleted later, also without Kjuup.io (disconnect upgrades to a purge).
        out.rows.push({
          key: "purge",
          label: busy.disconnecting ? tr("teams_disconnecting", "Disconnecting…") : tr("teams_disconnect_delete", "Delete imported chats"),
          icon: "delete",
          danger: true,
          busy: !!busy.disconnecting,
          onPress: handlers.purge,
        });
      }
      break;
    case "removing":
      out.banner = { tone: "info", icon: "delete_sweep", title: tr("teams_status_removing", "Removing your imported Teams chats…"), body: tr("teams_removing_body", "You can connect Microsoft Teams again when this is finished.") };
      break;
    case "connecting":
      out.progress = { value: 0, label: tr("teams_status_connecting", "Setting up the connection…"), sub: tr("teams_progress_hint", "Recent chats come first, older messages follow. You can leave this page.") };
      out.rows.push(disconnectRow);
      break;
    case "importing": {
      const p = teamsProgress(account.backfillProgress);
      const parts = [];
      if (p.total > 0) parts.push(fill(tr("teams_progress_chats", "{{done}} of {{total}} chats"), { done: p.done, total: p.total }));
      if (p.messages > 0) parts.push(fill(tr("teams_progress_messages", "{{count}} messages imported"), { count: p.messages }));
      const historyNext = account.backfillState === "recent_done" || p.phase === "history";
      out.progress = {
        value: p.value,
        label: historyNext ? tr("teams_status_importing_history", "Importing older messages…") : tr("teams_status_importing", "Importing your Teams chats…"),
        sub: parts.length ? parts.join(" · ") : tr("teams_progress_hint", "Recent chats come first, older messages follow. You can leave this page."),
      };
      if (!locked) out.rows.push(syncRow);
      out.rows.push(disconnectRow);
      break;
    }
    case "connected":
      if (account.backfillState === "failed") {
        out.banner = { tone: "warn", title: tr("teams_import_failed_web", "Not all chats could be imported. Click Sync now to try again.") };
      }
      if (!locked) out.rows.push(syncRow);
      out.rows.push(disconnectRow);
      break;
    case "consent":
      if (approved) {
        out.banner = {
          tone: "ok",
          icon: "verified",
          title: tr("teams_status_approved", "Your IT approved Kjuup"),
          body: tr("teams_approved_body", "Kjuup is now approved for your organisation. Connect your Microsoft account to finish."),
        };
        if (!locked) out.primary.push(connect(connectLabel));
        out.rows.push(disconnectRow);
        break;
      }
      out.banner = {
        tone: "warn",
        icon: "admin_panel_settings",
        title: tr("teams_status_consent", "Approval by your IT admin needed"),
        body: tr("teams_consent_body", "Your organization requires an IT admin to approve Kjuup for Microsoft Teams. Send them the approval link, then connect again."),
      };
      if (!locked) {
        out.primary.push({ key: "copyConsent", label: tr("teams_consent_copy", "Copy link for IT admin"), onPress: handlers.copyConsent, busy: !!busy.consent, icon: "content_copy" });
        out.primary.push({ key: "openConsent", label: tr("teams_consent_open", "Open approval page"), onPress: handlers.openConsent, disabled: !!busy.consent, kind: "secondary", icon: "open_in_new" });
        out.primary.push(connect(tr("teams_connect_again", "Connect again"), "secondary"));
      }
      out.rows.push(disconnectRow);
      break;
    case "policy":
      out.banner = {
        tone: "err",
        icon: "block",
        title: tr("teams_status_policy", "Blocked by your organization"),
        body: tr("teams_policy_body", "Your organization's policy does not allow connecting Kjuup to Microsoft Teams. Please contact your IT admin."),
      };
      if (!locked) out.primary.push(connect(tr("teams_connect_again", "Connect again"), "secondary"));
      out.rows.push(disconnectRow);
      break;
    case "reconnect":
    default:
      out.banner = {
        tone: "err",
        title: tr("teams_status_reconnect", "Reconnect needed"),
        body: tr("teams_reconnect_body", "Kjuup can no longer reach your Teams chats. They stay read-only until you reconnect."),
      };
      if (!locked) out.primary.push(connect(reconnectLabel));
      out.rows.push(disconnectRow);
      break;
  }

  if (errorBanner) {
    // The connect error goes first; a state banner of its own stays under it as the note.
    if (out.banner && !out.note) out.note = out.banner.body || out.banner.title;
    out.banner = errorBanner;
  }
  return out;
}

// A connect error the account states do not already explain (consent / policy show their own banner).
function teamsErrorBanner(error, states) {
  if (!error) return null;
  if (error.code === "consent_required" && states.includes("consent")) return null;
  if (error.code === "policy_blocked" && states.includes("policy")) return null;
  return { tone: error.code === "access_denied" ? "warn" : "err", title: error.message };
}

/**
 * The settings page for any number of Microsoft accounts.
 *
 * input: as buildTeamsSettingsView, but
 *   accounts    GET /teams/accounts list
 *   busy        { connecting, syncing, disconnecting, consent }: the id of the account busy with it
 *               ('new' for a connect of another account)
 *   handlers    each one called with the account (connect with null = add another account)
 * output:
 *   banner      page banner (connect error, Teams not available) or null
 *   primary     buttons when no account is listed yet (Connect Microsoft Teams)
 *   cards       [{ id, account, state, banner, progress, note, primary, rows }] one per account
 *   add         "Add another account" button, or null
 */
export const TEAMS_NEW_ACCOUNT = "new";

export function buildTeamsAccountsView({ accounts, enabled = true, locked = null, error = null, busy = {}, handlers = {}, strings = null }) {
  const tr = (key, fallback) => teamsText(strings, key, fallback);
  const list = teamsVisibleAccounts(accounts);
  const call = (name, account) => () => handlers[name] && handlers[name](account);
  const connectingNew = busy.connecting === TEAMS_NEW_ACCOUNT;

  if (!list.length) {
    const view = buildTeamsSettingsView({
      account: null,
      enabled,
      locked,
      error,
      strings,
      busy: { connecting: connectingNew },
      handlers: { connect: call("connect", null) },
    });
    return { banner: view.banner, primary: view.primary, cards: [], add: null };
  }

  const isBusy = (key, account) => busy[key] != null && String(busy[key]) === String(account.id);
  const cards = list.map((account) => {
    const view = buildTeamsSettingsView({
      account,
      enabled,
      locked,
      strings,
      multi: true,
      busy: {
        connecting: isBusy("connecting", account),
        syncing: isBusy("syncing", account),
        disconnecting: isBusy("disconnecting", account),
        consent: isBusy("consent", account),
      },
      handlers: {
        connect: call("connect", account),
        sync: call("sync", account),
        disconnect: call("disconnect", account),
        purge: call("purge", account),
        copyConsent: call("copyConsent", account),
        openConsent: call("openConsent", account),
        shareConsent: call("shareConsent", account),
      },
    });
    return { id: String(account.id), ...view };
  });

  const add =
    enabled && !locked
      ? {
          key: "add",
          label: connectingNew ? tr("teams_connecting", "Connecting…") : tr("teams_add_account", "Add another Microsoft account"),
          onPress: call("connect", null),
          busy: connectingNew,
          disabled: busy.connecting != null && !connectingNew,
          kind: "secondary",
          icon: "add",
        }
      : null;
  return { banner: teamsErrorBanner(error, cards.map((c) => c.state)), primary: [], cards, add };
}
